import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { get } from 'lodash';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { MonitorsService } from '@nrwl/monitor-management';
import { BaseSetupTab, IValueChangedEvent, MultiSitePickerComponent } from '@nrwl/test-setup';
import { NetworkTestService } from '../../network-test.service';
import { SitesService, ThresholdsService, IThresholdData, Debounce } from '@nrwl/common-lib';
import { NetworkKPIFieldMapping } from '../../json/network-thresholds';
import { takeUntil } from 'rxjs/operators';

export interface INetworkThresholdData {
  thresholdValue: number;
  minValue: number;
  maxValue: number;
  precision: number;
}

@Component({
  selector: 'nrwl-network-test',
  templateUrl: './network-test.component.html',
  styleUrls: ['./network-test.component.scss']
})
export class NetworkTestComponent implements OnInit {
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  @ViewChild('currentForm', { static: true }) form: NgForm;
  @ViewChild('thresholdForm', { static: true }) thresholdForm: NgForm;
  // eslint-disable-next-line
  destinationValue: BehaviorSubject<any>;
  // eslint-disable-next-line
  originatorValue: BehaviorSubject<any>;
  //eslint-disable-next-line
  @Input() componentId: string;
  @Input() tab: BaseSetupTab;
  //eslint-disable-next-line
  originatorDialogRef: MatDialogRef<any>;
  //eslint-disable-next-line
  destinationdialogRef: MatDialogRef<any>;
  selectedOriginator = [];
  selectedDestination = [];
  originatorInfo = {
    title: '',
    label: '',
    subLabel: ''
  };
  destinationInfo = {
    title: '',
    label: '',
    subLabel: ''
  };
  originatorConfiguration = {
    title: 'Originator',
    label: 'Select Originator',
    message: 'Choose a site or agent that will initiate the test',
    icon: 'mdi mdi-star-four-points'
  };
  destinationConfiguration = {
    title: 'Destination',
    label: 'Select Destination',
    message: 'Choose a site or agent that will initiate the test',
    icon: 'mdi mdi-map-marker'
  };
  nameExist: boolean;
  testName: string;
  allTestNames: Array<string>;
  editMode: boolean;
  cacheObj: Subscription;
  staticThresholds: IThresholdData[];
  networkKPIDatasource = {};
  actualTestName: string;
  private readonly _unsubscribe: Subject<void> = new Subject();
  //eslint-disable-next-line
  constructor(
    private dialog: MatDialog,
    private monitorsService: MonitorsService,
    private networkTestService: NetworkTestService,
    private sitesService: SitesService,
    private thresholdsService: ThresholdsService
  ) {
    // eslint-disable-next-line
    this.destinationValue = new BehaviorSubject<any>([]);
    // eslint-disable-next-line
    this.originatorValue = new BehaviorSubject<any>([]);
  }

  ngOnInit(): void {
    this.getStaticThresholds();
    this.cacheObj = this.monitorsService.cache.subscribe(data => {
      this.allTestNames = data.map(item => item.escapedName);
      if (this.testName) {
        this.checkIfNameAlreadyExist();
      }
    });
    this.tab.form = this.form;
    this.editMode = get(this.networkTestService.defaultParamsById, 'editMode', false);
    const defaultParams = this.networkTestService.defaultParamsById;
    if (defaultParams?.testFrom && !this.editMode) {
      this.populateAgent();
    }
    if (this.editMode) {
      this.populateNetworkTestdata();
    }
  }

  getMonitorMaxLimit(): void {
    this.networkTestService
      .getMonitorMaxLimit$()
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(
        response => {
          this.monitorsService.setcountResponse(response);
        },
        () => {}
      );
  }

  getStaticThresholds(): void {
    this.thresholdsService
      .getThresholds$()
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(result => {
        if (result) {
          this.staticThresholds = result;
          this.populateNetworkKpiData();
        }
      });
  }

  populateNetworkKpiData(): void {
    NetworkKPIFieldMapping.forEach(data => {
      const kpiName = data.kpiName;
      const kpiThreshold = this.staticThresholds.find(
        threshold => threshold.kpiname === kpiName && threshold.dimensions === data.dimensions
      );
      this.networkKPIDatasource[data.fieldName] = {
        thresholdValue: this.getThresholdValue(kpiThreshold, data.unit),
        minValue: kpiThreshold?.minValue || 0,
        maxValue:
          data.unit === 'ms' && kpiThreshold?.maxValue
            ? kpiThreshold.maxValue / 1000
            : kpiThreshold?.maxValue || 100,
        precision: kpiThreshold?.precision || 0,
        unit: data?.unit || 'ms',
        kpiThreshold: kpiThreshold
      };
    });
    this.onDataEmit('networkThreshold', this.networkKPIDatasource, true);
  }

  getThresholdValue(kpiThreshold: IThresholdData, unit: string): number {
    let thresholdValue = 0;
    let staticValues = null;
    if (kpiThreshold) {
      if (this.editMode) {
        const siteId = this.networkTestService.defaultParamsById.originatorInfo?.srcSiteId;
        if (siteId) {
          staticValues = siteId + ',' + this.networkTestService.defaultParamsById.monitorId;
        } else {
          //ATW-5188- Added static siteid for device without sites
          staticValues = '9999' + ',' + this.networkTestService.defaultParamsById.monitorId;
        }
      }
      const severityThresholds = this.findByValue(
        kpiThreshold.severityThresholds,
        'staticValues',
        staticValues
      );
      // check no threshold available for edit mode for older network test
      if (this.editMode && !severityThresholds) {
        this.onDataEmit('noThresholdExists', true, true);
      }
      thresholdValue = severityThresholds
        ? this.findByValue(severityThresholds.severityThresholdPairs, 'severity', 'Critical')
            ?.threshold
        : kpiThreshold.defaultCriticalValue;
      thresholdValue =
        unit === 'ms' && thresholdValue
          ? this.roundOffValue(thresholdValue / 1000, kpiThreshold.precision)
          : thresholdValue;
    }

    return thresholdValue;
  }

  //eslint-disable-next-line
  findByValue(entity: any, field: string, value: string): any {
    return entity.find(data => data[field] === value);
  }

  hasThresholdData(): boolean {
    return this.networkKPIDatasource && Object.keys(this.networkKPIDatasource).length > 0;
  }

  nameUpdated(): void {
    this.nameExist = false;
    this.onDataEmit('testName', null, false);
    if (this.testName) {
      const pattern = new RegExp('[?%/]');
      if (pattern.test(this.testName)) {
        this.form.controls.testName?.setErrors({ regex: 'wrong' });
        this.form.controls.testName?.markAsDirty();
      }
      this.checkIfNameAlreadyExist();
    }
  }
  checkIfNameAlreadyExist(): void {
    if (this.editMode) {
      this.allTestNames = this.allTestNames.filter(name => name !== this.actualTestName);
    }
    if (this.allTestNames.some(item => item.toLowerCase() === this.testName.toLowerCase())) {
      this.onDataEmit('testName', null, false);
      this.nameExist = true;
    } else {
      this.nameExist = false;
      this.onDataEmit('testName', this.testName, true);
    }
  }
  viewOriginator(): void {
    this.originatorDialogRef = this.dialog.open(MultiSitePickerComponent, {
      width: '100rem',
      height: '66rem',
      disableClose: true,
      data: {
        data: this.selectedOriginator,
        hidingData:
          this.selectedDestination && this.selectedDestination.length > 0
            ? this.selectedDestination[0]
            : null,
        singleSelection: true,
        hidingDeviceType: ['WINDOWS'],
        testType: 'network'
      }
    });
    this.originatorDialogRef.afterClosed().subscribe(selectedRows => {
      if (selectedRows) {
        this.selectedOriginator = selectedRows;
        this.originatorValue.next(selectedRows);

        this.setOriginator();
      }
    });
  }

  viewDestination(): void {
    this.destinationdialogRef = this.dialog.open(MultiSitePickerComponent, {
      width: '100rem',
      height: '66rem',
      disableClose: true,
      data: {
        data: this.selectedDestination,
        hidingData:
          this.selectedOriginator && this.selectedOriginator.length > 0
            ? this.selectedOriginator[0]
            : null,
        singleSelection: true,
        hidingDeviceType: ['WINDOWS'],
        testType: 'network'
      }
    });
    this.destinationdialogRef.afterClosed().subscribe(selectedRows => {
      if (selectedRows) {
        this.selectedDestination = selectedRows;
        this.setDestination();
      }
    });
  }

  setOriginator(): void {
    const selectedInfo = this.selectedOriginator[0];
    this.originatorValue.next(this.selectedOriginator);
    this.originatorInfo.title =
      selectedInfo.itemType === 'device' ? selectedInfo.name.label : selectedInfo.site.label;
    this.originatorInfo.label = selectedInfo.itemType === 'device' ? selectedInfo.site.label : '';
    this.originatorInfo.subLabel = selectedInfo.site.sublabel;
    this.onDataEmit('originator', selectedInfo, true);
  }

  setDestination(): void {
    const selectedInfo = this.selectedDestination[0];
    this.destinationValue.next(this.selectedDestination);
    this.destinationInfo.title =
      selectedInfo.itemType === 'device' ? selectedInfo.name.label : selectedInfo.site.label;
    this.destinationInfo.label = selectedInfo.itemType === 'device' ? selectedInfo.site.label : '';
    this.destinationInfo.subLabel = selectedInfo.site.sublabel;
    this.onDataEmit('destination', selectedInfo, true);
  }

  populateNetworkTestdata(): void {
    this.onDataEmit('monitorId', get(this.networkTestService.defaultParamsById, 'monitorId'), true);
    this.testName = get(this.networkTestService.defaultParamsById, 'testName', '');
    this.actualTestName = get(this.networkTestService.defaultParamsById, 'testName', '');
    this.onDataEmit('testName', this.testName, true);
    this.onDataEmit('testType', get(this.networkTestService.defaultParamsById, 'testType'), true);
    this.destinationInfo = get(this.networkTestService.defaultParamsById, 'destinationInfo', {});
    this.selectedDestination.push(this.destinationInfo);
    this.originatorInfo = get(this.networkTestService.defaultParamsById, 'originatorInfo', {});
    this.onDataEmit('originator', this.originatorInfo['data'], true);
    this.selectedOriginator.push(this.originatorInfo);
    const output = {};
    output['agent'] = this.destinationInfo['data'].agentSelector;
    output['name'] = this.destinationInfo['data'].label;
    output['phonenumber'] = null;
    this.onDataEmit('destination', output, true);
    this.sitesService
      .getValuesAsync()
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(data => {
        let originatorLocation = '';
        let destinationLocation = '';
        const srcSiteInfo = data.find(
          item => item.sitesData.id === this.originatorInfo['srcSiteId']
        );
        const originatorSite = srcSiteInfo?.sitesData?.id || '';
        this.onDataEmit('originatorSite', originatorSite, true);
        if (srcSiteInfo) {
          originatorLocation += srcSiteInfo.sitesData.location.city
            ? srcSiteInfo.sitesData.location.city + ', '
            : '';
          originatorLocation += srcSiteInfo.sitesData.location.state
            ? srcSiteInfo.sitesData.location.state + ', '
            : '';
          originatorLocation += srcSiteInfo.sitesData.location.country
            ? srcSiteInfo.sitesData.location.country
            : '';
          this.originatorInfo.subLabel = originatorLocation;
        }

        const dstSiteInfo = data.find(
          item => item.sitesData.id === this.destinationInfo['destSiteId']
        );
        if (dstSiteInfo) {
          destinationLocation += dstSiteInfo.sitesData.location.city
            ? dstSiteInfo.sitesData.location.city + ', '
            : '';
          destinationLocation += dstSiteInfo.sitesData.location.state
            ? dstSiteInfo.sitesData.location.state + ', '
            : '';
          destinationLocation += dstSiteInfo.sitesData.location.country
            ? dstSiteInfo.sitesData.location.country
            : '';
          this.destinationInfo.subLabel = destinationLocation;
        }
      });
    this.setOrgDestData();
  }

  setOrgDestData(): void {
    this.originatorValue.next(this.selectedOriginator);
    this.destinationValue.next(this.selectedDestination);
  }
  // eslint-disable-next-line
  onDataEmit(key: string, newValue: any, isValid: boolean): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      key: key,
      newValue: newValue,
      isValid: isValid
    });
  }

  @Debounce(500)
  thresholdValueUpdated(fieldName: string): void {
    this.networkKPIDatasource[fieldName].thresholdValue = this.roundOffValue(
      this.networkKPIDatasource[fieldName].thresholdValue,
      this.networkKPIDatasource[fieldName].precision
    );
    this.onDataEmit('networkThreshold', this.networkKPIDatasource, this.thresholdForm.valid);
  }

  roundOffValue(data: number, precision: number): number {
    return data ? +data?.toFixed(precision) : data;
  }

  getTwoWayValue(originatorField: string, destinationField: string): string {
    const totalVaue =
      this.networkKPIDatasource[originatorField].thresholdValue +
      this.networkKPIDatasource[destinationField].thresholdValue;
    return (
      this.roundOffValue(totalVaue, this.networkKPIDatasource[originatorField].precision) +
      this.networkKPIDatasource[originatorField].unit
    );
  }

  populateAgent(): void {
    const defaultParams = this.networkTestService.defaultParamsById;
    defaultParams.testFrom === 'originator'
      ? this.selectedOriginator.push(defaultParams)
      : this.selectedDestination.push(defaultParams);
    if (defaultParams.testFrom === 'originator') {
      this.setOriginator();
    } else {
      this.setDestination();
    }
  }
}
